import axios from '../axios';

// ---------------------------------------------------------------------------
// Financeiro / Régua de cobrança (billing dunning) — Service Layer completo.
//
// Handoff 0090 + delta (docs/design/design_handoff_cobrancas_vencidas/):
// CRM kanban de títulos vencidos. Caso = cliente (agrupa os títulos vencidos
// dele); o caso nasce e é baixado por webhook do ASAAS — o operador só move de
// raia, registra tratativa e agenda compromisso.
//
// Contrato FC1: sucesso desembrulhado aqui (`data.data`); erro propaga cru para
// a página ler via `readApiError`/`financeErrorMessage` (padrão do módulo
// Financeiro em `pages/Finance/utils/apiError.js`). camel↔snake só nestas
// margens; a página trabalha com os nomes do backend (snake) nos OBJETOS de
// resposta — que já chegam prontos — e com camelCase nos argumentos.
//
// ⚠️ D14 (delta): `next_action_at` grava por UM caminho só — `updateDunning`
// (PATCH /:id). O POST de interações descarta a chave silenciosamente e o
// PATCH /:id/stage só aceita `stage_id`. Não "aproveitar" outra chamada para
// gravar compromisso.
//
// ⚠️ TRILHA (handoff 0103 — 2026-08-28): o caso tem `track` (INTERNAL =
// régua da casa, EXTERNAL = escritório de cobrança) e o MESMO cliente pode ter
// um caso aberto em cada trilha. Título que vence entra SEMPRE na interna; ele
// só cruza a fronteira por decisão explícita do gestor, e o que atravessa é o
// TÍTULO, não o caso (`escalate`/`return`). O caso que fica sem nenhum título
// aberto porque tudo migrou encerra com `status: 'TRANSFERRED'`.
// ---------------------------------------------------------------------------

const BASE = '/finance/billing-dunnings';
const STAGES_BASE = '/finance/billing-dunning-stages';
const RECONCILIATION_BASE = '/finance/billing-dunning-reconciliation';

// Toda mutação de caso responde o caso inteiro atualizado (shape do GET /:id).
// Leitura defensiva da chave do envelope para não acoplar no nome exato.
function readCase(response) {
   const data = response?.data?.data ?? {};
   return data.billing_dunning ?? data.dunning ?? data;
}

function readMeta(response) {
   const meta = response?.data?.meta ?? {};
   return {
      total: meta.total ?? 0,
      page: meta.page ?? 1,
      perPage: meta.per_page ?? 0,
      totalPages: meta.total_pages ?? 1,
   };
}

// ─── Casos ─────────────────────────────────────────────────────────────────

// Kanban/fila. Um fetch alimenta as DUAS visões (o kanban agrupa por stage_id
// no cliente). Sem `orderBy`, a ordenação é a default do backend:
// next_action_at ASC, id ASC, nulls primeiro (fila de urgência). Com `orderBy`
// (handoff 0091), o backend ordena o CONJUNTO COMPLETO no SQL — whitelist:
// customer_name | interactions_count | open_count | open_amount |
// oldest_due_date | settled_amount (0102); valor fora do enum é 422, não
// fallback.
//
// Os casos trazem, desde 0102, os agregados de caso ENCERRADO: `settled_count`/
// `settled_amount`/`last_payment_date` e `cancelled_count`/`cancelled_amount`.
// São aditivos — quem lê `open_*` continua igual. A tela escolhe o bloco pelo
// `status` (utils/derive → `caseFigures`).
//
// `track` (0103 §6) filtra a trilha; ausente = as DUAS, que é o board completo
// — a raia EXTERNAL já separa visualmente o que está com o escritório.
export async function listDunnings({
   search,
   status,
   stageId,
   assignedUserId,
   actionStatus,
   track,
   orderBy,
   orderDir,
   page,
   perPage,
} = {}) {
   const params = {};
   if (search) params.search = search;
   if (status) params.status = status;
   if (stageId) params.stage_id = stageId;
   if (track) params.track = track;
   // 'none' não existe no contrato — o filtro "Sem responsável" é sempre id
   // numérico ou ausência. Quem trata a opção "none" é a tela.
   if (assignedUserId) params.assigned_user_id = assignedUserId;
   if (actionStatus) params.action_status = actionStatus;
   if (orderBy) {
      params.order_by = orderBy;
      if (orderDir) params.order_dir = orderDir;
   }
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;

   const response = await axios.get(BASE, { params });
   return {
      dunnings: response?.data?.data?.billing_dunnings ?? [],
      // Contagem por raia com os MESMOS filtros (exceto stage_id/paginação) e
      // o escopo de atribuição embutido — raia fora do array = zero casos.
      // `null` = backend sem o campo ainda (distinto de [] = tudo vazio).
      stageCounts: response?.data?.data?.stage_counts ?? null,
      meta: readMeta(response),
   };
}

// Card completo: mesmo shape da listagem + `interactions` (DESC, sem paginação).
//
// Os membros trazem o acerto (0103 §4.2): `checked`, `checked_at`,
// `checked_note` e `checked_by` — este último SÓ aqui, o kanban não carrega o
// join por custo. Fora do detalhe, use o booleano `checked`.
export async function getDunning(id) {
   const response = await axios.get(`${BASE}/${id}`);
   return readCase(response);
}

// Candidatos a responsável (handoff 0100 §3.2). Fonte PRÓPRIA do módulo — não
// é `GET /users`: só entra quem tem `finance:dunning:assignable`. Escolher
// alguém de fora dessa lista dá 422 `user_not_assignable` no PATCH.
//
// Exige só `finance:dunning:read` (a mesma da rota), então a régua deixou de
// depender da permissão de listar usuários. Sem paginação e sem filtros — a
// lista é curta por natureza. Shape idêntico ao `assigned_user` do card.
export async function listDunningAssignees() {
   const response = await axios.get(`${BASE}/assignees`);
   return response?.data?.data?.assignees ?? [];
}

// Timeline C4 (auditoria). Paginada — a tela busca uma página generosa e monta
// o histórico com merge de interações + eventos.
export async function listDunningEvents(id, { page, perPage } = {}) {
   const params = {};
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;

   const response = await axios.get(`${BASE}/${id}/events`, { params });
   return {
      events: response?.data?.data?.events ?? [],
      meta: readMeta(response),
   };
}

// Compromisso + responsável — o ÚNICO caminho que escreve `next_action_at`.
// `null` é valor válido (remove o compromisso / desatribui); por isso o teste é
// `!== undefined`, não truthiness.
//
// RBAC fatiado (0100): as duas chaves do body pedem permissões DIFERENTES —
// `next_action_at` é `finance:dunning:interact`, `assigned_user_id` é
// `finance:dunning:update` (+ `read_all`). Quem não pode reatribuir NÃO manda a
// chave: o backend responde 403 `assignment_change_forbidden` a qualquer toque
// nela, e o 403 derruba a tela toda pelo interceptor global.
export async function updateDunning(id, { nextActionAt, assignedUserId } = {}) {
   const body = {};
   if (nextActionAt !== undefined) body.next_action_at = nextActionAt;
   if (assignedUserId !== undefined) body.assigned_user_id = assignedUserId;

   const response = await axios.patch(`${BASE}/${id}`, body);
   return readCase(response);
}

// Drag do kanban. Body é SÓ stage_id (D14). Erros esperados: 422
// terminal_stage_is_automatic / already_in_stage e, desde 0103, 422
// external_stage_not_movable (a raia EXTERNAL deixou de ser destino de
// movimentação) / external_case_not_movable (caso da trilha externa não muda de
// raia). A tela bloqueia os três alvos antes de chamar.
export async function moveDunningStage(id, stageId) {
   const response = await axios.patch(`${BASE}/${id}/stage`, {
      stage_id: stageId,
   });
   return readCase(response);
}

// ─── Trilha: mover TÍTULO entre interna e externa (handoff 0103) ───────────
//
// O que atravessa a fronteira é o título, nunca o caso — daí a lista de ids
// obrigatória (1..200; "todos" = mandar todos). Substituiu o antigo
// POST /:id/send-external, que escalava o caso inteiro sem escolha e por isso
// arrastava junto todo título que vencesse depois.
//
// ⚠️ As duas respondem o caso de DESTINO, não o da URL: é onde o gestor
// continua trabalhando. O caso de ORIGEM também mudou (perdeu títulos, e pode
// ter encerrado como TRANSFERRED) e NÃO vem na resposta — quem chama precisa
// refetchar a lista, não só aplicar o caso devolvido.
//
// Permissão das duas: `finance:dunning:external` (quem decide entregar decide
// retomar).

// Interna → externa. Origem precisa ser INTERNAL (422 wrong_track_for_transfer)
// e estar aberta (422 dunning_not_open); todo id precisa ser membro ABERTO do
// caso (422 charge_not_in_dunning).
export async function escalateDunning(id, chargeIds) {
   const response = await axios.post(`${BASE}/${id}/escalate`, {
      charge_ids: chargeIds,
   });
   return readCase(response);
}

// Externa → interna. Mesmo contrato, papéis trocados: a origem precisa ser
// EXTERNAL e o caso interno recriado nasce na raia ENTRY (o gestor reposiciona
// depois, se quiser).
export async function returnDunning(id, chargeIds) {
   const response = await axios.post(`${BASE}/${id}/return`, {
      charge_ids: chargeIds,
   });
   return readCase(response);
}

// Alimenta a confirmação ANTES de escalar/devolver: o cliente já tem caso na
// trilha de destino, ou vai nascer um? `direction` e `target_track` vêm
// derivados da trilha do caso de origem — a tela não precisa deduzir.
// `target` é o caso de destino no mesmo shape do card, ou `null` (caso novo).
// Só exige `finance:dunning:read`.
export async function getDunningTransferTarget(id) {
   const response = await axios.get(`${BASE}/${id}/transfer-target`);
   const data = response?.data?.data ?? {};
   return {
      direction: data.direction ?? null,
      targetTrack: data.target_track ?? null,
      target: data.target ?? null,
   };
}

// ─── Acerto com o escritório (handoff 0103 §5.4–5.6) ───────────────────────
// Permissão `finance:dunning:reconcile` — separada de `:interact` de propósito:
// quem trata o caso (inclusive o operador do escritório) não confere o próprio
// trabalho.

// Conferir/desconferir um título do acerto. Só vale para membro SETTLED de caso
// da trilha EXTERNAL — e funciona TAMBÉM em caso encerrado, porque o acerto
// acontece depois do pagamento, quando o caso já fechou. Responde o caso
// inteiro, como toda mutação do módulo.
export async function checkDunningCharge(id, chargeId, { checked, note } = {}) {
   const body = { checked };
   if (note) body.note = note;

   const response = await axios.patch(
      `${BASE}/${id}/charges/${chargeId}/check`,
      body
   );
   return readCase(response);
}

// Relatório do acerto — a linha é o TÍTULO, não o caso.
//
// ⚠️ `from`/`to` filtram a data da CONFERÊNCIA (`checked_at`), não o vencimento
// nem o pagamento. Por isso `checked: false` + intervalo é 422 no backend (e a
// tela impede a combinação, em vez de deixar o operador tomar o erro).
//
// `summary` é do conjunto filtrado INTEIRO, não da página — é o número que o
// gestor leva para pagar o escritório.
export async function listDunningReconciliation({
   checked,
   from,
   to,
   customerDoc,
   page,
   perPage,
} = {}) {
   const params = {};
   // `false` é valor válido (títulos ainda não conferidos) — testar por
   // `!== undefined`, nunca por truthiness.
   if (checked !== undefined) params.checked = checked;
   if (from) params.from = from;
   if (to) params.to = to;
   if (customerDoc) params.customer_doc = customerDoc;
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;

   const response = await axios.get(RECONCILIATION_BASE, { params });
   const data = response?.data?.data ?? {};
   return {
      charges: data.reconciliation_charges ?? [],
      summary: {
         count: data.summary?.count ?? 0,
         totalAmount: data.summary?.total_amount ?? 0,
      },
      meta: readMeta(response),
   };
}

// Mesmos filtros, sem paginação. Responde um .xlsx binário — `responseType:
// 'blob'` mantém a resposta fora do caminho de leitura do envelope JSend.
// Teto de 5.000 linhas no backend; acima disso a planilha traz uma linha de
// AVISO de truncamento no final.
export async function exportDunningReconciliation({
   checked,
   from,
   to,
   customerDoc,
} = {}) {
   const params = {};
   if (checked !== undefined) params.checked = checked;
   if (from) params.from = from;
   if (to) params.to = to;
   if (customerDoc) params.customer_doc = customerDoc;

   const response = await axios.get(`${RECONCILIATION_BASE}/export`, {
      params,
      responseType: 'blob',
   });
   return response.data;
}

// Tratativa (append-only — não existe editar/excluir). `moveToStageId` faz o
// "registrar e avançar" atômico; nunca aceita a raia EXTERNAL como destino, e
// desde 0103 nem sequer é aceito em caso da TRILHA externa (422
// external_case_not_movable — o caso do escritório não muda de raia).
//
// ⚠️ `moveToStageId` exige `finance:dunning:update` (0100) — registrar tratativa
// exige apenas `:interact`. Como a operação é ATÔMICA, mandar a chave sem a
// permissão devolve 403 `stage_move_requires_permission` e NADA é gravado: o
// operador perde o relato que digitou. Por isso o campo some do formulário para
// quem não pode mover raia, em vez de falhar no submit.
//
// ANEXOS (handoff 0096): com `files`, o mesmo endpoint aceita multipart —
// campos de texto + `files` repetido (até 3, 10MB cada). SEM arquivo, segue
// JSON como sempre (o contrato antigo não mudou). Tudo é atômico: se um
// arquivo é rejeitado, nada é gravado — por isso a tela mantém o estado do
// formulário no 422.
//
// Campos opcionais só entram quando têm valor: FormData transforma tudo em
// string, e um `charge_id=""` viraria valor inválido no backend.
export async function createDunningInteraction(
   id,
   {
      channel,
      content,
      promisedPaymentDate,
      chargeId,
      moveToStageId,
      files,
   } = {},
   { onUploadProgress } = {}
) {
   const hasFiles = Array.isArray(files) && files.length > 0;

   if (!hasFiles) {
      const body = { channel, content };
      if (promisedPaymentDate) body.promised_payment_date = promisedPaymentDate;
      if (chargeId) body.charge_id = chargeId;
      if (moveToStageId) body.move_to_stage_id = moveToStageId;

      const response = await axios.post(`${BASE}/${id}/interactions`, body);
      return readCase(response);
   }

   const formData = new FormData();
   formData.append('channel', channel);
   formData.append('content', content);
   if (promisedPaymentDate) {
      formData.append('promised_payment_date', promisedPaymentDate);
   }
   if (chargeId) formData.append('charge_id', String(chargeId));
   if (moveToStageId) formData.append('move_to_stage_id', String(moveToStageId));
   // Mesmo nome de campo repetido por arquivo (multer .array('files')).
   files.forEach((file) => formData.append('files', file));

   const response = await axios.post(`${BASE}/${id}/interactions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
   });
   return readCase(response);
}

// ─── Anexos da tratativa (handoff 0096) ────────────────────────────────────

// Download: a key do S3 nunca chega ao front — o backend devolve uma URL
// presigned de 15min. Pedir no clique e NUNCA cachear (a URL azeda).
export async function getDunningFileDownload(id, interactionId, fileId) {
   const response = await axios.get(
      `${BASE}/${id}/interactions/${interactionId}/files/${fileId}/download`
   );
   const data = response?.data?.data ?? {};
   return { url: data.url ?? null, filename: data.filename ?? null };
}

// Exclusão com rastreio: motivo obrigatório (3–255) no BODY do DELETE. O
// arquivo some do S3, mas a linha fica no caso com `deleted: true` + quem/
// quando/porquê. Responde o caso inteiro, como toda mutação do módulo.
export async function deleteDunningFile(
   id,
   interactionId,
   fileId,
   { reason } = {}
) {
   const response = await axios.delete(
      `${BASE}/${id}/interactions/${interactionId}/files/${fileId}`,
      { data: { reason } }
   );
   return readCase(response);
}

// ─── Raias ─────────────────────────────────────────────────────────────────

export async function listDunningStages() {
   const response = await axios.get(STAGES_BASE);
   return response?.data?.data?.billing_dunning_stages ?? [];
}

// Criação é sempre de raia ATTEMPT (o tipo não é escolhível).
export async function createDunningStage({ name, position, autoAdvanceOnDue } = {}) {
   const body = { name };
   if (position !== undefined) body.position = position;
   if (autoAdvanceOnDue !== undefined) body.auto_advance_on_due = autoAdvanceOnDue;

   const response = await axios.post(STAGES_BASE, body);
   return response?.data?.data ?? null;
}

export async function updateDunningStage(
   id,
   { name, position, autoAdvanceOnDue, isActive } = {}
) {
   const body = {};
   if (name !== undefined) body.name = name;
   if (position !== undefined) body.position = position;
   if (autoAdvanceOnDue !== undefined) body.auto_advance_on_due = autoAdvanceOnDue;
   if (isActive !== undefined) body.is_active = isActive;

   const response = await axios.put(`${STAGES_BASE}/${id}`, body);
   return response?.data?.data ?? null;
}

export async function deleteDunningStage(id) {
   await axios.delete(`${STAGES_BASE}/${id}`);
}
